import PropTypes from 'prop-types';
import React, { useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { Button, Form, Modal } from 'semantic-ui-react';
import { isEmpty, startCase, camelCase, filter, find } from 'lodash';

import AmsModal from '../../../utils/AmsModal';
import { addLookup, fetchAmsLookup } from '../../../actions/lookupActions';
import AmsFormLabel from '../../../utils/AmsFormLabel';

const category = 'languages';

const FormUtils = ({
  addLookup,
  fetchAmsLookup,
  amsLookups,
  setError,
  onNewLanguageSaved,
  onClose,
}) => {
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [newLanguage, setNewLanguage] = useState({});
  const [saveNewLanguageEnabled, setSaveNewLanguageEnabled] = useState(false);
  const [addingNewLanguage, setAddingNewLanguage] = useState(false);
  const [showOtherAddModal, setShowOtherAddModal] = useState(true);

  const toPascalCase = str => {
    return startCase(camelCase(str.toLowerCase())).replace(/ /g, '');
  };

  const handleAddLanguageChange = e => {
    const { value } = e.target;
    const cameledValue = toPascalCase(value);
    e.target.value = cameledValue;

    setSaveNewLanguageEnabled(!!cameledValue);
    setErrors({});

    const data = {
      value: {
        label: cameledValue,
        value: cameledValue,
      },
      category: category,
      valueType: 'Object',
      active: true,
      parent: false,
    };

    setNewLanguage(data);
  };

  const validateNewLanguage = data => {
    const validationErrors = {};

    if (isEmpty(data?.value?.value)) {
      validationErrors.newLanguageError = 'Value is required.';
      return validationErrors;
    }

    const isValid = /^[a-zA-Z_.-]+$/.test(data?.value?.value);
    if (!isValid) {
      validationErrors.newLanguageError = `${data?.value?.value} value should be alphabetical with no spaces`;
      return validationErrors;
    }

    fetchAmsLookup(category).catch(error => {
      if (setError) {
        setError({
          responseError: error.response.data.message,
        });
      }
    });

    const lookups = amsLookups[category] || [];
    const parent = find(lookups, { parent: true });
    const activeLookups = filter(lookups, { parent: false, active: true });

    if (parent?.active && activeLookups.length) {
      const isDuplicate = activeLookups.some(
        lookup => lookup.value.value === data?.value?.value
      );

      if (isDuplicate) {
        validationErrors.newLanguageError = `${data?.value?.value} value is duplicated.`;
      }
    }

    return validationErrors;
  };

  const saveNewLanguage = () => {
    const validationErrors = validateNewLanguage(newLanguage);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      setAddingNewLanguage(true);

      addLookup(newLanguage)
        .then(() => {
          fetchAmsLookup(category)
            .then(() => {
              setSelectedLanguage(newLanguage?.value?.value);
              setAddingNewLanguage(false);
              setShowOtherAddModal(false);
              setErrors({});

              if (onNewLanguageSaved) {
                onNewLanguageSaved(newLanguage?.value?.value);
              }
              if (onClose) {
                onClose();
              }
            })
            .catch(error => {
              if (setError) {
                setError({
                  responseError: error.response.data.message,
                });
              }
            });
        })
        .catch(error => {
          setAddingNewLanguage(false);
          setShowOtherAddModal(false);
          setErrors(prev => ({
            ...prev,
            parentError: error.response,
          }));
        });
    }
  };

  const closeOtherAddModal = useCallback(() => {
    setShowOtherAddModal(false);
    setErrors({});
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  const showOtherAddLanguageModal = () => (
    <AmsModal
      open={showOtherAddModal}
      onClose={closeOtherAddModal}
      closeOnEscape={false}
      closeOnDimmerClick={false}
      className="ams-semantic-modal-fix"
    >
      <Modal.Header>Specify Language</Modal.Header>
      <Modal.Content>
        <Form loading={loading || addingNewLanguage}>
          <Form.Group widths="equal">
            <Form.Field error={!!errors?.newLanguageError} required>
              <AmsFormLabel
                fieldLabel="languageName"
                name="Specify Language (eg. English, Spanish, etc.)"
                error={errors?.newLanguageError}
              >
                <div>
                  <input
                    name="newLanguageName"
                    onChange={handleAddLanguageChange}
                    placeholder="Specify Language"
                    id="newLanguageName"
                    aria-labelledby="newLanguageName"
                  />
                  {errors?.newLanguageError && (
                    <p style={{ color: 'red' }}>{errors?.newLanguageError}</p>
                  )}
                </div>
              </AmsFormLabel>
            </Form.Field>
          </Form.Group>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button
          primary
          onClick={saveNewLanguage}
          disabled={!saveNewLanguageEnabled}
        >
          Save
        </Button>
        <Button onClick={closeOtherAddModal}>Close</Button>
      </Modal.Actions>
    </AmsModal>
  );

  return <div>{showOtherAddLanguageModal()}</div>;
};

FormUtils.propTypes = {
  addLookup: PropTypes.func.isRequired,
  fetchAmsLookup: PropTypes.func.isRequired,
  amsLookups: PropTypes.object.isRequired,
  setError: PropTypes.func,
  onNewLanguageSaved: PropTypes.func,
  onClose: PropTypes.func,
};

const mapStateToProps = state => ({
  currentUser: state.auth.user,
  amsLookups: state.lookups.amsLookups,
});

export default connect(mapStateToProps, {
  addLookup,
  fetchAmsLookup,
})(FormUtils);
